import { z } from "zod/v4";
import { defineEndpoint, defineResource } from "@visioni/a3s-client-api";
import { stringToInt } from "@visioni/a3s-codecs";
import { FEATURE_NAME } from "../feature-name";

/**
 * Starts a Patriot backfill: a one-off reconciler run over a bounded range,
 * for the lanes asked for. Support-only, behind `patriot_tools_backfill`.
 *
 * Declared here rather than imported from the reconciler so the wire contract
 * is this module's, per docs/api.practices.md. The server pins
 * {@link PATRIOT_BACKFILL_LANES} to the reconciler's own lane list at compile
 * time.
 */
export const PATRIOT_BACKFILL_LANES = [
  "signals",
  "activations",
  "operatorNotes",
  "clients",
  "actionPlans",
  "clientGroupingTypes",
  "clientGroupings",
] as const;
export type PatriotBackfillLane = (typeof PATRIOT_BACKFILL_LANES)[number];

/** Lanes with no date in Patriot's API: a backfill reads every record and ignores any range. */
export const UNDATED_PATRIOT_BACKFILL_LANES: readonly PatriotBackfillLane[] = [
  "clients",
  "actionPlans",
  "clientGroupingTypes",
  "clientGroupings",
];

export const StartPatriotBackfillRequest = z
  .object({
    lanes: z
      .array(z.enum(PATRIOT_BACKFILL_LANES))
      .nonempty()
      .refine((lanes) => new Set(lanes).size === lanes.length, "Name each lane once"),
    /** Any time, to the minute or finer. Required once any lane is dated; undated lanes ignore it. */
    start: z.iso.datetime({ offset: true }).optional(),
    /** Patriot's date filters include a record stamped exactly at `end`. Given exactly when `start` is. */
    end: z.iso.datetime({ offset: true }).optional(),
    /** Re-emit every in-range record, even one already stored and unchanged. */
    force: z.boolean().optional(),
  })
  .refine((request) => (request.start === undefined) === (request.end === undefined), {
    message: "Give both ends of the range, or neither",
    path: ["end"],
  })
  .refine(
    (request) =>
      request.start !== undefined || request.lanes.every((lane) => UNDATED_PATRIOT_BACKFILL_LANES.includes(lane)),
    { message: "Signals, activations and operator notes need a range", path: ["start"] },
  )
  .refine(
    (request) =>
      request.start === undefined || request.end === undefined || Date.parse(request.start) < Date.parse(request.end),
    { message: "The range must end after it starts", path: ["end"] },
  );
export type StartPatriotBackfillRequest = z.output<typeof StartPatriotBackfillRequest>;

export const StartPatriotBackfillResponse = z.object({
  /** When the API handed the run to the reconciler. The run itself starts moments later. */
  startedAt: z.iso.datetime(),
  backfill: StartPatriotBackfillRequest,
});
export type StartPatriotBackfillResponse = z.output<typeof StartPatriotBackfillResponse>;

/** Under `commands/`: it writes to the replica. The path's tenant plays no part. */
export const patriotBackfillResource = defineResource({
  feature: FEATURE_NAME,
  resource: "patriot/commands/backfill",
  scope: "tenant",
});

/**
 * Answers as soon as the reconciler has the run, not when it finishes: a run
 * can take up to 15 minutes, well past the API's 30s.
 */
export const startPatriotBackfillEndpoint = defineEndpoint(patriotBackfillResource, {
  method: "POST",
  bodySchema: StartPatriotBackfillRequest,
  responseSchema: StartPatriotBackfillResponse,
});

/** One backfill from its record: who started it, with which options, and when. */
export const PatriotBackfillRun = z.object({
  runId: z.string(),
  /** Absent for a run started outside Patriot tools. */
  requestedBy: z.string().optional(),
  /** The requester's address, where it could be found. */
  requestedByEmail: z.string().optional(),
  lanes: z.array(z.enum(PATRIOT_BACKFILL_LANES)),
  start: z.string().optional(),
  end: z.string().optional(),
  force: z.boolean(),
  /** When the reconciler started the run, moments after it was asked for. */
  startedAt: z.string(),
});
export type PatriotBackfillRun = z.output<typeof PatriotBackfillRun>;

export const ListPatriotBackfillsQueryParams = z.object({
  limit: stringToInt.pipe(z.int().positive().max(100)).default(20),
  cursor: z.string().optional(),
});
export type ListPatriotBackfillsQueryParamsWire = z.input<typeof ListPatriotBackfillsQueryParams>;
export type ListPatriotBackfillsQueryParams = z.output<typeof ListPatriotBackfillsQueryParams>;

export const ListPatriotBackfillsResponse = z.object({
  /** Newest first. */
  items: z.array(PatriotBackfillRun),
  /** Opaque resume token; `null` when there are no older runs. */
  cursor: z.string().nullable(),
});
export type ListPatriotBackfillsResponse = z.output<typeof ListPatriotBackfillsResponse>;

/** Fleet-wide like the fleet activations: the path's tenant plays no part. */
export const patriotBackfillsResource = defineResource({
  feature: FEATURE_NAME,
  resource: "patriot/fleet/backfills",
  scope: "tenant",
});

export const listPatriotBackfillsEndpoint = defineEndpoint(patriotBackfillsResource, {
  responseSchema: ListPatriotBackfillsResponse,
  querySchema: ListPatriotBackfillsQueryParams,
});
